import { Type } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormControlState, FormGroup, StatusChangeEvent, ValidatorFn } from '@angular/forms';
import { Arrayify, Assert } from '@sb/library/data-models';
import { Observable, Subject, defaultIfEmpty, filter, forkJoin, map, of, startWith, switchMap, take, tap } from 'rxjs';
import { DialogBaseViewComponent } from '../../views/dialog-base-view.component';
import { ErrorKey } from './form-validation.definitions';
import { Validators } from './form-validators.definitions';

type ControlType<T> = T extends AbstractControl<infer U> ? (U extends FormControlState<any> ? never : U) : never;

type ValidationOptions = {
    DisableIfValid?: boolean;
    ScrollToFirstError?: boolean;
    MarkAsSaving?: boolean;
}

const defaultValidationOptions: ValidationOptions = {
    DisableIfValid: true,
    ScrollToFirstError: true,
    MarkAsSaving: true
}

export enum FormSaveState {
    Unsaved,
    Saving,
    Saved,
    Cancelled
}

declare module '@angular/forms' {

    interface FormGroup<TControl extends { [K in keyof TControl]: AbstractControl }> {
        CheckValidity(options?: ValidationOptions): Observable<boolean>;
        AddControl<U>(this: FormGroup<TControl>, formControlName: string, defaultValue: U, isRequired?: boolean): FormControl<U>;
        SetDefaultValueToCurrent(): void;
        Reset(): void;
        Disable(): void;
        Enable(): void;
        SaveSucceeded(overwriteDefault?: boolean): void;  // overwriteDefault defaults to false. Only want this for the Save/Discard paradigm, e.g. Business Settings
        SaveFailed(): void;
        Cancelled(): void;
        autoSave: boolean;

        readonly errorCount: number;
        readonly saveMethod?: () => void;
        readonly saveSubject?: Subject<boolean>;
        readonly saveState: FormSaveState;
        readonly unsavedChangesDialog?: Type<DialogBaseViewComponent>;
    }

    interface FormArray<TControl extends AbstractControl> {
        Add(this: FormArray<TControl>, item?: ControlType<TControl>): void;
    }

    interface FormControl<TValue> {
        isRequired: boolean;
        SetApiError(): void;
        Disable(): void;
        Enable(): void;
        ConnectToForm(form: FormGroup): void;
        Autofill(value: TValue): void;
        AddValidators(validators: ValidatorFn | ValidatorFn[]): void;
        AddDefaultValidators(validators: ValidatorFn | ValidatorFn[]): void;
        RemoveValidators(validators: ValidatorFn | ValidatorFn[]): void;
    }

    interface AbstractControl<TValue> {
        isAutofilled: boolean;
        isDirty: boolean;
        isLoading: boolean;
        isReadOnly: boolean;
        isViewOnly: boolean;
        CustomPostValidationCheck?: () => Observable<boolean>;
    }

    interface ValidatorFn {
        validatorKey?: ErrorKey
    }

    /** @internal */
    interface FormGroup<TControl extends { [K in keyof TControl]: AbstractControl }> {
        defaultControls: Record<string, AbstractControl>;
        connectedControl?: FormControl;
        controlsWithErrors: Set<AbstractControl>;
        
        _errorCount: number;
        _saveMethod?: () => void;
        _saveSubject?: Subject<boolean>;
        _saveState: FormSaveState;
        _unsavedChangesDialog?: Type<DialogBaseViewComponent>;
    }

    /** @internal */
    interface FormArray<TControl extends AbstractControl> {
        generator: () => AbstractControl;
        defaultControlValue: any;
        defaultControls: AbstractControl[];
        controlsWithErrors: Set<AbstractControl>;
        _errorCount: number;
    }

    /** @internal */
    interface FormControl<TValue> {
        connectedForm?: FormGroup;
        defaultDisabledState?: boolean;
        _defaultValue?: any;
        _namedValidators: Map<ErrorKey, ValidatorFn>;
        RegisterForErrorCount: () => void;
    }

    /** @internal */
    interface AbstractControl {
        _isAutofilled: boolean;
        _isDirty: boolean;
        _isLoading: boolean;
        _isReadOnly: boolean;
        _isRequired: boolean;
        _isViewOnly: boolean;
        cachedDisabled: boolean;
        defaultCachedDisabled: boolean;
    }
}

function CacheForDisable(control: AbstractControl) {
    if (control.disabled) {
        control.cachedDisabled = true;
    } else if (control instanceof FormGroup || control instanceof FormArray) {
        Object.values(control.controls).forEach(CacheForDisable);
    }
}

function ReenableFromCache(control: AbstractControl) {
    if (control.disabled) {
        if (control.cachedDisabled) {
            control.cachedDisabled = false;
        } else if (control instanceof FormGroup || control instanceof FormArray) {
            const controls = Object.values(control.controls);
            if (controls.length) {
                controls.forEach(ReenableFromCache);
            } else {
                control.enable();
            }
        } else {
            control.enable();
        }
    }
}

/** @internal */
export function CacheDefault(control: AbstractControl) {
    control.defaultCachedDisabled = control.cachedDisabled;

    const isFormGroup = control instanceof FormGroup;
    if (isFormGroup || control instanceof FormArray) {
        control.defaultControls = isFormGroup ? {...control.controls} : [...control.controls];
        Object.values(control.controls).forEach(CacheDefault);
    } else {
        Assert(control instanceof FormControl);
        const controlValue = control.value;
        control.defaultDisabledState = control.disabled;

        // Need to ensure that no aliasing (i.e., shared reference) takes place for subform controls
        if (control.connectedForm && typeof controlValue === 'object' && controlValue !== null) {
            control._defaultValue = {...controlValue};
        } else {
            control._defaultValue = controlValue;
        }
    }
}

function RestoreDefault(control: AbstractControl) {
    control.cachedDisabled = control.defaultCachedDisabled;

    const isFormGroup = control instanceof FormGroup;
    if (isFormGroup || control instanceof FormArray) {
        control.controls = isFormGroup ? {...control.defaultControls} : [...control.defaultControls];
        const controls = Object.values(control.controls);
        if (controls.length) {
            controls.forEach(RestoreDefault);
        } else {
            control.enable();
        }
    } else {
        Assert(control instanceof FormControl);
        if (control.defaultDisabledState) {
            control.disable();
        } else {
            control.enable();
        }

        // Need to ensure that no aliasing (i.e., shared reference) takes place for subform controls
        if (control.connectedForm && typeof control._defaultValue === 'object' && control._defaultValue !== null) {
            control.setValue({...control._defaultValue});
        } else {
            control.setValue(control._defaultValue);
        }
    }
}

function MarkAllDirty(control: AbstractControl): void {
    if (!control.disabled) {
        if (control instanceof FormGroup || control instanceof FormArray) {
            Object.values(control.controls).forEach(MarkAllDirty);
        } else {
            Assert(control instanceof FormControl);
            if (control.connectedForm) {
                MarkAllDirty(control.connectedForm!);
            }
        }

        control._isDirty = true;
        control.markAsTouched({ onlySelf: true, emitEvent: false });

        // Preserve any manually-set errors (e.g., from setErrors) when validators run.
        // Only restore keys that are not managed by a named validator or by constructor-added
        // required/requiredTrue validators — those are re-evaluated by updateValueAndValidity
        // and should not be forced back if the user has corrected them.
        const previousErrors = control.errors;
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        if (previousErrors) {
            const namedKeys = new Set((control as FormControl)._namedValidators?.keys() ?? []);
            const manualErrors = Object.fromEntries(
                Object.entries(previousErrors).filter(([key]) =>
                    !namedKeys.has(key as ErrorKey) && key !== 'required' && key !== 'requiredTrue' && key !== 'subform'
                )
            );
            if (Object.keys(manualErrors).length) {
                const merged = { ...(control.errors ?? {}), ...manualErrors };
                control.setErrors(merged);
            }
        }
    }
}

function RunAllCustomPostValidationChecks(control: AbstractControl): Observable<boolean> {
    const checks: Observable<boolean>[] = [];
    if (control.CustomPostValidationCheck) {
        checks.push(control.CustomPostValidationCheck().pipe(take(1)));
    }
    if (control instanceof FormGroup || control instanceof FormArray) {
        checks.push(...Object.values(control.controls).map(RunAllCustomPostValidationChecks));
    }
    return forkJoin(checks).pipe(defaultIfEmpty([]), map(results => results.every(result => result)));
}

function FixAngularDisableBug(control: AbstractControl) {
    if (control instanceof FormGroup || control instanceof FormArray) {
        Object.values(control.controls).forEach(FixAngularDisableBug);
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
}

function SetControlsWithErrors(control: AbstractControl, controlToSet: AbstractControl = control) {
    const parent = control.parent;
    if (parent) {
        parent.controlsWithErrors = new Set(parent.controlsWithErrors);
        if (controlToSet.isDirty && controlToSet.invalid) {
            parent.controlsWithErrors.add(controlToSet);
        } else {
            parent.controlsWithErrors.delete(controlToSet);
        }
        parent._errorCount = parent.controlsWithErrors.size;
        SetControlsWithErrors(parent, controlToSet);
    }
}

export function ScrollToFirstError(): void {
    setTimeout(() => { // we set a timeout so the errors show up
        //We get the all the invalid forms
        var elements = document.getElementsByClassName('sb-form-field-error-wrapper');
        if (elements.length > 0) {
            //We get this first invalid form
            var requiredElement = elements[0];
            //We get the parent so, we can include the label
            var formField = requiredElement.closest('lib-form-field-container') as HTMLElement | null;
            // formField?.scrollIntoView({behavior: "smooth"});
            const rect = formField?.getBoundingClientRect();

            const drawerLayout = document.getElementsByClassName('scss-drawer-layout')[0] as HTMLElement;
            const applicationLayout = document.getElementById('main-application-content');

            var scrollContainer: HTMLElement | null = null;

            if (drawerLayout) {
                scrollContainer = drawerLayout.parentElement;
            } else if (applicationLayout) {
                // Item view or other top-level component
                scrollContainer = applicationLayout;
            } else { // widgets
                formField?.scrollIntoView();
                return;
            }

            if (rect && (rect.top || rect.bottom) && scrollContainer) {
                const headerBottom =  (document.getElementsByClassName('scss-drawer-header')[0] as HTMLElement)?.getBoundingClientRect()?.bottom ?? 0;
                const additionalHeaderBottom = document.getElementById('additional-drawer-header')?.getBoundingClientRect()?.bottom ?? 0;
                let headerOffset = headerBottom + additionalHeaderBottom + 60;
                let footerOffset = window.innerHeight;

                const drawerFooterTop = (document.getElementsByClassName('scss-drawer-footer')[0] as HTMLElement)?.getBoundingClientRect()?.top ?? 0;
                if (drawerFooterTop > 0 && drawerFooterTop < window.innerHeight) {
                    footerOffset = drawerFooterTop;
                }

                if (rect.bottom > footerOffset) {
                    scrollContainer.scrollBy({ top: rect.bottom - footerOffset, behavior: 'smooth' });
                } else if (rect.top < headerOffset) {
                    scrollContainer.scrollBy({ top: rect.top - headerOffset, behavior: 'smooth' });
                }
            }
        }
    }, 500);
}

FormArray.prototype.Add = function<T>(item?: T): AbstractControl<T> {
    if (item === undefined) {
        item = this.defaultControlValue as T;
    }

    let control: AbstractControl<T>;
    if (this.generator) {
        control = this.generator();
    } else {
        if (!this._isRequired) {
            control = new FormControl<T>(item, { nonNullable: true });
        } else if (typeof item === 'boolean') {
            control = new FormControl<T>(item, { nonNullable: true, validators: Validators.requiredTrue });
        } else {
            control = new FormControl<T>(item, { nonNullable: true, validators: Validators.required });
        }
        (control as FormControl).isRequired = this._isRequired;
    }

    control.patchValue(item);

    control.isLoading = this._isLoading;
    control.isReadOnly = this._isReadOnly;
    control.isViewOnly = this._isViewOnly;
    control['_parent'] = this;

    if (this.disabled) {
        control.disable();
    }

    this.controls.push(control);
    return control;
}

FormGroup.prototype.CheckValidity = function(options: ValidationOptions): Observable<boolean> {
    MarkAllDirty(this);
    const optionsWithDefaults = {...defaultValidationOptions, ...options};
    if (optionsWithDefaults.MarkAsSaving) {
        this._saveState = FormSaveState.Saving;
    }

    return this.statusChanges.pipe(
        startWith(this.status),
        filter(status => status !== 'PENDING'),
        take(1),
        switchMap(_ => {
            if (this.invalid) {
                if (optionsWithDefaults.ScrollToFirstError) {
                    ScrollToFirstError();
                }

                this.SaveFailed();
                return of(false);
            }

            return RunAllCustomPostValidationChecks(this).pipe(tap(result => {
                if (!result) {
                    this.SaveFailed();
                } else if (optionsWithDefaults.DisableIfValid) {
                    this.Disable();
                    if (!optionsWithDefaults.MarkAsSaving) {
                        this.SaveFailed();
                    }
                }
            }));
        }));
}

FormGroup.prototype.AddControl = function<T>(formControlName: string, defaultValue: T, isRequired: boolean = false): FormControl<T> {
    const control = new FormControl<T>(defaultValue, { nonNullable: true });

    control.isLoading = this.isLoading;
    control.isReadOnly = this.isReadOnly;
    control.isViewOnly = this.isViewOnly;

    if (isRequired) {
        control.isRequired = true;
    }

    if (this.disabled) {
        control.disable();
    }

    this.addControl(formControlName, control);
    return control;
}

FormGroup.prototype.SetDefaultValueToCurrent = function(): void {
    CacheDefault(this);
    this.isDirty = false;
    this._saveState = FormSaveState.Unsaved;
}

FormGroup.prototype.Reset = function(): void {
    RestoreDefault(this);
    this.isDirty = false;
    this._saveState = FormSaveState.Unsaved;
}

FormGroup.prototype.Enable = function(): void {
    this.cachedDisabled = false;
    ReenableFromCache(this);
}

FormGroup.prototype.Disable = function(): void {
    if (!this.disabled) {
        CacheForDisable(this);

        // Angular disable has a bug where simply calling disable may not actually disable the form, so we also call
        // updateValueAndValidity on it and all of its subforms
        this.disable();
        FixAngularDisableBug(this);
    }
}

FormGroup.prototype.SaveFailed = function(): void {
    if (this.parent instanceof FormGroup) {
        this.parent.SaveFailed();
    } else {
        this._saveState = FormSaveState.Unsaved;
        this.saveSubject?.next(false);
    }
}

FormGroup.prototype.SaveSucceeded = function(overwriteDefault: boolean = false): void {
    this.isAutofilled = false;
    this._saveState = FormSaveState.Saved;
    if (overwriteDefault) {
        this.SetDefaultValueToCurrent();
    }
    this.saveSubject?.next(true);
}

FormGroup.prototype.Cancelled = function(): void {
    this.Disable();
    this._saveState = FormSaveState.Cancelled;
}

FormControl.prototype.SetApiError = function() {
    setTimeout(() => {
        this.setErrors({api: true});
        ScrollToFirstError();
    });
}

FormControl.prototype.ConnectToForm = function(form: FormGroup) {
    this.connectedForm = form;
    form.connectedControl = this;
}

FormControl.prototype.Enable = function(): void {
    this.cachedDisabled = false;
    this.enable();
}

FormControl.prototype.Disable = function(): void {
    this.disable();
}

FormControl.prototype.Autofill = function(value: any): void {
    this.patchValue(value);
    this.isAutofilled = this.value !== null && this.value !== undefined && this.value !== '';
}

function UpdateRequired(control: FormControl) {
    control.isRequired = control.hasValidator(Validators.required) || control.hasValidator(Validators.requiredTrue);
    control.updateValueAndValidity({ emitEvent: false });
}

function AddValidators(control: FormControl, isDefault: boolean, validators: ValidatorFn | ValidatorFn[]) {
    if (!control._namedValidators) {
        control._namedValidators = new Map();
    }

    Arrayify(validators).forEach(validator => {
        if (validator.validatorKey) {
            const existing = control._namedValidators.get(validator.validatorKey);
            if (existing) {
                if (isDefault) {
                    return;  // Default validators do not override; continue forEach loop
                } else {
                    control.removeValidators(existing);
                }
            }
            control._namedValidators.set(validator.validatorKey, validator);
        }

        control.addValidators(validator);
    });

    UpdateRequired(control);
}


FormControl.prototype.AddValidators = function(validators: ValidatorFn | ValidatorFn[]): void {
    AddValidators(this, false, validators);
}

FormControl.prototype.AddDefaultValidators = function(validators: ValidatorFn | ValidatorFn[]): void {
    AddValidators(this, true, validators);
}

FormControl.prototype.RemoveValidators = function(validators: ValidatorFn | ValidatorFn[]): void {
    Arrayify(validators).forEach(validator => {
        if (validator.validatorKey && this._namedValidators) {
            const stored = this._namedValidators.get(validator.validatorKey);
            if (stored) {
                this.removeValidators(stored);
                this._namedValidators.delete(validator.validatorKey);
            }
        } else {
            this.removeValidators(validator);
        }
    });

    UpdateRequired(this);
}

FormControl.prototype.RegisterForErrorCount = function() {
    this.events.pipe(filter(event => event instanceof StatusChangeEvent)).subscribe(event => {
        SetControlsWithErrors(this);
    });
}

Object.defineProperties(FormGroup.prototype, {
    autoSave: { value: false, writable: true },
    connectedControl: { writable: true },
    controlsWithErrors: { value: new Set(), writable: true },
    _errorCount: { value: 0, writable: true },
    _saveState: { value: FormSaveState.Unsaved, writable: true },
    _saveMethod: { writable: true },
    _saveSubject: { writable: true },
    _unsavedChangesDialog: { writable: true },

    errorCount: { get(this: FormGroup) { return this._errorCount; } },
    saveState: { get(this: FormGroup) { return this._saveState; } },
    saveMethod: { get(this: FormGroup) { return this._saveMethod; } },
    saveSubject: { get(this: FormGroup) { return this._saveSubject; } },
    unsavedChangesDialog: { get(this: FormGroup) { return this._unsavedChangesDialog; } },

    isAutofilled: {
        get(this: FormGroup) { return this._isAutofilled; },
        set(this: FormGroup, value: boolean) {
            this._isAutofilled = value; // propagates downward if false, upward if true
            if (!value) {
                Object.values(this.controls).forEach(control => control.isAutofilled = value);
            } else {
                if (this.parent) {
                    this.parent.isAutofilled = value;
                } else if (this.connectedControl) {
                    this.connectedControl.isAutofilled = value;
                }
            }
        }
    },
    isDirty: {
        get(this: FormGroup) { return (this._isDirty || this._isAutofilled) && !this.disabled; },
        set(this: FormGroup, value: boolean) {
            this._isDirty = value;
            if (!value) { // propagates downward if false, upward if true
                Object.values(this.controls).forEach(control => control.isDirty = value);
            } else {
                if (this.parent) {
                    this.parent.isDirty = value;
                } else if (this.connectedControl) {
                    this.connectedControl.isDirty = value;
                }
            }
        }
    },
    isLoading: {
        get(this: FormGroup) { return this._isLoading; },
        set(this: FormGroup, value: boolean) {
            this._isLoading = value;
            Object.values(this.controls).forEach(control => control.isLoading = value);
        }
    },
    isReadOnly: {
        get(this: FormGroup) { return this._isReadOnly },
        set(this: FormGroup, value: boolean) {
            this._isReadOnly = value;
            Object.values(this.controls).forEach(control => control.isReadOnly = value);
        }
    },
    isViewOnly: {
        get(this: FormGroup) { return this._isViewOnly; },
        set(this: FormGroup, value: boolean) {
            this._isViewOnly = value;
            Object.values(this.controls).forEach(control => control.isViewOnly = value);
        }
    }
});

Object.defineProperties(FormArray.prototype, {
    defaultControlValue: { value: null, writable: true },
    defaultControls: { value: [], writable: true },
    controlsWithErrors: { value: new Set(), writable: true },
    _errorCount: { value: 0, writable: true },

    isAutofilled: {
        get(this: FormArray) { return this._isAutofilled; },
        set(this: FormArray, value: boolean) {
            this._isAutofilled = value; // propagates downward if false, upward if true
            if (!value) {
                this.controls.forEach(control => control.isAutofilled = value);
            } else if (this.parent) {
                this.parent.isAutofilled = value;
            }
        }
    },
    isDirty: {
        get(this: FormArray) { return (this._isDirty || this._isAutofilled) && !this.disabled; },
        set(this: FormArray, value: boolean) {
            this._isDirty = value; // propagates downward if false, upward if true
            if (!value) {
                this.controls.forEach(control => control.isDirty = value);
            } else if (this.parent) {
                this.parent.isDirty = value;
            }
        }
    },
    isLoading: {
        get(this: FormArray) { return this._isLoading; },
        set(this: FormArray, value: boolean) {
            this._isLoading = value;
            this.controls.forEach(control => control.isLoading = value);
        }
    },
    isReadOnly: {
        get(this: FormArray) { return this._isReadOnly; },
        set(this: FormArray, value: boolean) {
            this._isReadOnly = value;
            this.controls.forEach(control => control.isReadOnly = value);
        }
    },
    isViewOnly: {
        get(this: FormArray) { return this._isViewOnly; },
        set(this: FormArray, value: boolean) {
            this._isViewOnly = value;
            this.controls.forEach(control => control.isViewOnly = value);
        }
    }
});

Object.defineProperties(FormControl.prototype, {
    connectedForm: { writable: true },
    defaultControls: { value: {}, writable: true },
    defaultDisabledState: { writable: true },
    isRequired: { value: false, writable: true },
    _defaultValue: { writable: true },
    _namedValidators: { value: null, writable: true },

    isAutofilled: {
        get(this: FormControl) { return this._isAutofilled; },
        set(this: FormControl, value: boolean) {
            this._isAutofilled = value; // propagates downward if false, upward if true
            if (!value) {
                if (this.connectedForm) {
                    this.connectedForm.isAutofilled = value;
                }
            } else if (this.parent) {
                this.parent.isAutofilled = value;
            }
        }
    },
    isDirty: {
        get(this: FormControl) { return (this._isDirty || this._isAutofilled) && !this.disabled; },
        set(this: FormControl, value: boolean) {
            this._isDirty = value; // propagates downward if false, upward if true
            if (!value) {
                if (this.connectedForm) {
                    this.connectedForm.isDirty = value;
                }
            } else {
                SetControlsWithErrors(this);
                if (this.parent) {
                    this.parent.isDirty = value;
                    this._isAutofilled = false;
                }
            }
        }
    },
    isLoading: {
        get(this: FormControl) { return this._isLoading; },
        set(this: FormControl, value: boolean) {
            this._isLoading = value;
            if (this.connectedForm) {
                this.connectedForm.isLoading = value;
            }
        }
    },
    isReadOnly: {
        get(this: FormControl) { return this._isReadOnly; },
        set(this: FormControl, value: boolean) {
            this._isReadOnly = value;
            if (this.connectedForm) {
                this.connectedForm.isReadOnly = value;
            }
        }
    },
    isViewOnly: {
        get(this: FormControl) { return this._isViewOnly; },
        set(this: FormControl, value: boolean) {
            this._isViewOnly = value;
            if (this.connectedForm) {
                this.connectedForm.isViewOnly = value;
            }
        }
    }
});

Object.defineProperties(AbstractControl.prototype, {
    _isAutofilled: { value: false, writable: true },
    _isDirty: { value: false, writable: true },
    _isLoading: { value: false, writable: true },
    _isReadOnly: { value: false, writable: true },
    _isRequired: { value: false, writable: true },
    _isViewOnly: { value: false, writable: true },
    cachedDisabled: { value: false, writable: true },
    defaultCachedDisabled: { value: false, writable: true },
    CustomPostValidationCheck: { writable: true }
});
